import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { Sparkles, X, Check } from 'lucide-react';
import './system-update-toast.css';

const CURRENT_UPDATE_KEY = 'plotwise_update_ack_v20261023';

export interface SystemUpdateToastProps {
  isTourActive?: boolean;
}

// Kontroller som toasten aldrig må dække (samme udvalg som layout-auditten i tests/e2e/helpers).
const AVOID_SELECTOR = [
  'button',
  'a[href]',
  'input:not([type="hidden"])',
  'select',
  'textarea',
  '[role="button"]',
  '[role="tab"]',
  '[role="menuitem"]',
  '.leaflet-marker-icon.leaflet-interactive',
  '.map-search',
  '.map-tools',
  '.map-actionbar',
  '.field-inspector',
  '.layer-menu-popover',
].join(',');

/**
 * Desktop/tablet: find den nederste, højre-nærmeste plads i viewporten, hvor toasten ikke ligger oven på
 * en kontrol (topbar, kortværktøjer, handlingslinje, zoom). Mobilskallen placeres rent med CSS.
 */
function placeClearOfControls(el: HTMLElement) {
  for (const prop of ['left', 'top', 'right', 'bottom']) el.style.removeProperty(prop);
  el.removeAttribute('data-compact');
  if (document.querySelector('.mobile-app-shell')) return;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const rects = Array.from(document.querySelectorAll(AVOID_SELECTOR))
    .filter((c) => !el.contains(c) && c.getClientRects().length > 0)
    .map((c) => c.getBoundingClientRect())
    .filter((r) => r.width > 0 && r.height > 0 && r.bottom > 0 && r.right > 0 && r.top < vh);
  const GAP = 6;
  const STEP = 12;
  const tryPlace = () => {
    const w = el.offsetWidth;
    const h = el.offsetHeight;
    const hits = (x: number, y: number) =>
      rects.some(
        (r) =>
          x < r.right + GAP && x + w > r.left - GAP && y < r.bottom + GAP && y + h > r.top - GAP,
      );
    for (let y = vh - h - 24; y >= 76; y -= STEP) {
      for (let x = vw - w - 24; x >= 8; x -= STEP) {
        if (!hits(x, y)) {
          el.style.left = `${x}px`;
          el.style.top = `${y}px`;
          el.style.right = 'auto';
          el.style.bottom = 'auto';
          return true;
        }
      }
    }
    return false;
  };
  if (tryPlace()) return;
  // Ingen plads til den fulde udgave (fx med markoplysninger åbne): vis den kompakte uden beskrivelsen.
  el.setAttribute('data-compact', '');
  tryPlace();
}

export const SystemUpdateToast: React.FC<SystemUpdateToastProps> = ({ isTourActive }) => {
  const [visible, setVisible] = useState(false);
  const toastRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof localStorage === 'undefined') return;
    try {
      const acknowledged = localStorage.getItem(CURRENT_UPDATE_KEY);
      if (!acknowledged) {
        // Delay slightly for smooth non-jarring appearance
        const timer = setTimeout(() => setVisible(true), 1200);
        return () => clearTimeout(timer);
      }
    } catch {
      // ignore
    }
  }, []);

  const handleDismiss = () => {
    try {
      localStorage.setItem(CURRENT_UPDATE_KEY, new Date().toISOString());
    } catch {
      // ignore
    }
    setVisible(false);
  };

  const shown = visible && !isTourActive;
  useLayoutEffect(() => {
    const el = toastRef.current;
    if (!shown || !el) return;
    const place = () => placeClearOfControls(el);
    place();
    // Layoutet (kortværktøjer, paneler, markoplysninger) ændrer sig, mens appen bruges: genplacér efter
    // DOM-ændringer og afsluttede animationer, så toasten aldrig ender oven på en kontrol.
    let frame = 0;
    const schedule = () => {
      window.clearTimeout(frame);
      frame = window.setTimeout(place, 120);
    };
    const observer = new MutationObserver((records) => {
      if (records.some((r) => !el.contains(r.target))) schedule();
    });
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'hidden'],
    });
    window.addEventListener('resize', schedule);
    document.addEventListener('transitionend', schedule, true);
    document.addEventListener('animationend', schedule, true);
    return () => {
      window.clearTimeout(frame);
      observer.disconnect();
      window.removeEventListener('resize', schedule);
      document.removeEventListener('transitionend', schedule, true);
      document.removeEventListener('animationend', schedule, true);
    };
  }, [shown]);

  if (!shown) return null;

  return (
    <aside ref={toastRef} className="system-update-toast" role="status" aria-live="polite">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, flex: 1 }}>
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: 8,
            background: 'rgba(34, 197, 94, 0.15)',
            border: '1px solid rgba(34, 197, 94, 0.3)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#4ade80',
            flexShrink: 0,
          }}
        >
          <Sparkles size={16} />
        </div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div
            style={{
              fontSize: '0.84rem',
              fontWeight: 750,
              color: '#ffffff',
              lineHeight: 1.25,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <span>Plotwise er opdateret</span>
            <span
              style={{
                fontSize: '0.62rem',
                fontWeight: 800,
                padding: '1px 5px',
                borderRadius: 4,
                background: '#15803d',
                color: '#fff',
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
              }}
            >
              Ny version
            </span>
          </div>
          <p
            className="system-update-toast-desc"
            style={{
              margin: '2px 0 0',
              fontSize: '0.74rem',
              color: '#cbd5e1',
              lineHeight: 1.3,
            }}
          >
            Optimeret mobilvisning, plejeplaner og hurtigere kort. Systemet videreudvikles løbende.
          </p>
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
        <button
          type="button"
          onClick={handleDismiss}
          style={{
            background: '#15803d',
            border: '1px solid #22c55e',
            color: '#ffffff',
            borderRadius: 8,
            padding: '5px 10px',
            fontSize: '0.76rem',
            fontWeight: 700,
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            minHeight: 44,
          }}
          aria-label="Forstået, luk opdateringsbesked"
        >
          <Check size={13} />
          <span>Forstået</span>
        </button>
        <button
          type="button"
          onClick={handleDismiss}
          style={{
            background: 'transparent',
            border: 'none',
            color: '#94a3b8',
            padding: 4,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 44,
            minWidth: 44,
          }}
          aria-label="Afvis"
          className="system-update-toast-dismiss"
        >
          <X size={16} />
        </button>
      </div>
    </aside>
  );
};
